import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  FileText, 
  BookOpen, 
  DollarSign, 
  TrendingUp,
  Eye,
  Activity,
  Settings,
  LogOut,
  Plus,
  Search,
  Filter,
  Home,
  Image,
  GalleryHorizontal,
  Layers,
  ShieldCheck
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const backendUrl = "https://justurbane.in/backend_php/public";

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [isFullAdmin, setIsFullAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeDashboard = async () => {
      // 1. Check admin authentication
      const token = localStorage.getItem('admin_token');
      const user = localStorage.getItem('admin_user');
      const employeeId = localStorage.getItem('employee_id');
      
      if (!token || !user) {
        navigate('/admin/login');
        return;
      }

      setAdminUser(JSON.parse(user));

      // 2. Handle Permissions Logic
      // If employee_id is null/missing, it's the Master Admin
      if (!employeeId || employeeId === "null" || employeeId === "undefined") {
        setIsFullAdmin(true);
      } else {
        await fetchPermissions(token, employeeId);
      }

      await fetchDashboardStats(token);
      setLoading(false);
    };

    initializeDashboard();
  }, [navigate]);

  const fetchPermissions = async (token, employeeId) => {
    try {
      const res = await axios.get(`${backendUrl}/api/admin/employees/full`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.status) {
        const allEmployees = res.data.data || [];
        const currentEmp = allEmployees.find(emp => String(emp.employee_id) === String(employeeId));
        if (currentEmp) {
          setPermissions(currentEmp);
        }
      }
    } catch (error) {
      console.error("Permission fetch error:", error);
      toast.error("Failed to verify access levels");
    }
  };

  const fetchDashboardStats = async (token) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        throw new Error('Failed to fetch stats');
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    }
  };

  // Permission Helper
  const hasPermission = (key) => {
    if (isFullAdmin) return true;
    if (!permissions) return false;
    return permissions[key] === 1;
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('employee_id');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Syncing secure dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex items-center space-x-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_urbane-nexus/artifacts/w4pbaa92_Untitled%20design-10.png" 
                  alt="Just Urbane" 
                  className="h-10 w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div 
                  className="w-10 h-10 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg flex items-center justify-center"
                  style={{ display: 'none' }}
                >
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Just Urbane Admin</h1>
                  <p className="text-xs text-amber-600 font-bold flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> {isFullAdmin ? 'Super Admin' : permissions?.type_name}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>Welcome,</span>
                <span className="font-semibold text-gray-900">{adminUser?.full_name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          {hasPermission('article_management') && (
            <div className="bg-white rounded-xl shadow-sm p-6 border">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Articles</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.total_articles || 0}</p>
                </div>
              </div>
            </div>
          )}

          {hasPermission('magazine_management') && (
            <div className="bg-white rounded-xl shadow-sm p-6 border">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <BookOpen className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Magazines</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.total_magazines || 0}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Subscribers</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total_subscribers || 0}</p>
              </div>
            </div>
          </div>

          {hasPermission('orders_management') && (
            <div className="bg-white rounded-xl shadow-sm p-6 border">
              <div className="flex items-center">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-amber-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.total_revenue || 0)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {hasPermission('article_management') && (
                <button
                  onClick={() => navigate('/admin/articles/new')}
                  className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5 text-blue-500" />
                  <span className="text-gray-700">Create New Article</span>
                </button>
              )}
              
              {hasPermission('gallery_management') && (
                <button
                  onClick={() => navigate('/admin/gallery')}
                  className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <Image className="w-5 h-5 text-green-500" />
                  <span className="text-gray-700">Gallery</span>
                </button>
              )}

             {isFullAdmin && (
                <>
                  <button
                    onClick={() => navigate('/admin/employee_permissions')}
                    className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <ShieldCheck className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Manage Permissions</span>
                  </button>

                  <button
                    onClick={() => navigate('/admin/employees')}
                    className="w-full flex items-center space-x-3 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <Users className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-700">Manage Employees</span>
                  </button>
                </>
              )}

            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Articles</h3>
            <div className="space-y-3">
              {stats?.popular_articles?.slice(0, 3).map((article, index) => (
                <div key={article.id} className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {article.title}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center">
                      <Eye className="w-3 h-3 mr-1" />
                      {article.views} views
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {stats?.recent_activities?.slice(0, 3).map((activity, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.title}</p>
                    <p className="text-xs text-gray-500">{formatDate(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          
          {hasPermission('article_management') && (
            <button
              onClick={() => navigate('/admin/articles')}
              className="bg-white rounded-xl shadow-sm p-6 border hover:shadow-md transition-shadow text-left hover:scale-105 transform transition-transform"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Articles</h3>
                  <p className="text-sm text-gray-600">Manage content</p>
                </div>
              </div>
            </button>
          )}

          {hasPermission('magazine_management') && (
            <button
              onClick={() => navigate('/admin/digital_magazines')}
              className="bg-white rounded-xl shadow-sm p-6 border hover:shadow-md transition-shadow text-left hover:scale-105 transform transition-transform"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <BookOpen className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Magazines</h3>
                  <p className="text-sm text-gray-600">Digital issues</p>
                </div>
              </div>
            </button>
          )}

          {hasPermission('orders_management') && (
            <button
              onClick={() => navigate('/admin/orders')}
              className="bg-white rounded-xl shadow-sm p-6 border hover:shadow-md transition-shadow text-left hover:scale-105 transform transition-transform"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-pink-100 rounded-lg">
                  <Image className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Orders</h3>
                  <p className="text-sm text-gray-600">Order details</p>
                </div>
              </div>
            </button>
          )}
          
          {hasPermission('carousel_management') && (
            <button
              onClick={() => navigate('/admin/carousel')}
              className="bg-white rounded-xl shadow-sm p-6 border hover:shadow-md transition-shadow text-left hover:scale-105 transform transition-transform"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <GalleryHorizontal className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Carousel</h3>
                  <p className="text-sm text-gray-600">Manage banners</p>
                </div>
              </div>
            </button>
          )}

          {hasPermission('article_categories_management') && (
            <button
              onClick={() => navigate('/admin/categories')}
              className="bg-white rounded-xl shadow-sm p-6 border hover:shadow-md transition-shadow text-left hover:scale-105 transform transition-transform"
            >
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Layers className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Categories</h3>
                  <p className="text-sm text-gray-600">Manage taxonomy</p>
                </div>
              </div>
            </button>
          )}
          
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;